import React, { useCallback, useState } from 'react';
import { ProgramForm } from '@client/components/collections/ProgramForm';
import { CollectionProgram } from '@client/components/collections/collections.types';
import { OTPLandingPageBuilder } from '@client/pages/CustomProgramPage/OTPLandingPageBuilder';
import { goBack } from '@client/routes';
import { Typography, Tabs, Tab, CircularProgress } from '@mui/material';
import { useTabHash } from '../../hooks/useTabHash';
import { _noop, keys } from '../../helpers/general';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle as readyIcon } from '@fortawesome/pro-solid-svg-icons';
import {
  faCheckCircle as notReadyIcon,
  faArrowLeft,
} from '@fortawesome/pro-regular-svg-icons';
import { SuccessModal } from './publishModal/SuccessModal';
import { AsyncWorkouts } from './workouts/workouts.view';
import { CategoryForm } from '../../../components/collections/CategoryForm';
import { ProgramPricing } from './pricing/ProgramPricing';
import { PublishModal } from './publishModal/PublishModal';
import { Button } from '@design-system/components/button/button';
import { HeaderPage } from '../layouts';
import { ProgramPageProps } from './collection.viewModel';
import { ProgramTabHash, allTabs } from './domain';
import { ValidationAlert } from './alerts/validationAlert';
import { getTabWithWorkoutDefault } from './mappers';
import { ArchiveModal } from '@client/new/common/collection/ArchiveModal';
import { ChallengesTab } from './challenges/ChallengesTab';
import { SubscriptionPriceAlert } from './alerts/subscriptionPriceAlert';
import { TMap } from '@client/new/helpers/types';
import { PublishToBundle } from './PublishToBundleModal';
import { RemoveFromBundle } from './RemoveFromBundleModal';
import { OfferLandingPageBuilder } from '@client/pages/LPBuilder/view/OfferLandingPageBuilder';

export type StepState =
  | 'ready'
  | 'partially-ready'
  | 'not-ready'
  | 'not-available'
  | 'loading'
  | 'error';
export const stepStateColorsMap: TMap<StepState, string> = {
  ready: '#00CB20',
  'partially-ready': '#6367EF',
  'not-available': '#DEE2EB',
  'not-ready': '#B2B5BF',
  loading: '#fafafa',
  error: '#B2B5BF',
};

type TabContentProps = ProgramSummaryTabProps & {
  tabHash: ProgramTabHash;
  workouts: Array<any>;
  readonly: boolean;
  onLandingPageDirtyChange?: (isDirty: boolean) => void;
};

const TabContent = ({ tabHash, ...p }: TabContentProps) => {
  switch (tabHash) {
    case 'workouts-draft':
    case 'workouts-archived':
    case 'workouts-published':
    case 'workouts-scheduled':
      return (
        <div className="py-6">
          <AsyncWorkouts
            collection={p.collection}
            creatorId={p.collection.influencerId}
            readonly={p.readonly}
          />
        </div>
      );
    case 'about': {
      return (
        <div className="bg-white rounded-md p-6">
          {p.collection.type === 0 ? (
            <ProgramForm {...p} />
          ) : (
            <CategoryForm {...p} />
          )}
        </div>
      );
    }
    case 'pricing':
      return (
        <div>
          <ProgramPricing
            creatorId={p.collection.influencerId}
            collection={p.collection}
            onPriceSubmitted={p.refetchCollection}
            readonly={p.readonly}
          />
        </div>
      );
    case 'sales-page':
      return (
        <>
          {/* <OTPLPBuilderV2 programId={p.params.programId} /> */}
          <OTPLandingPageBuilder
            embedded
            params={{ ...p.params }}
            readonly={p.readonly}
            onDirtyChange={p.onLandingPageDirtyChange}
          />
        </>
      );
    case 'page-builder':
      return (
        <OfferLandingPageBuilder
          programId={parseInt(p.params.programId, 10)}
          creatorId={p.collection.influencerId}
          offerId={20}
        />
      );

    case 'challenges':
      return <ChallengesTab programId={p.collection.id} />;
  }
};

export type ProgramSummaryTabProps = {
  collection: CollectionProgram;
  originalAccessType: CollectionProgram['accessType'];
  readonly: boolean;
  disabledCollectionType: boolean;
  validationTree: any;
  router: any;
  route: unknown;
  onChange: (p: CollectionProgram) => void;
  typeChanged: (t: number) => void;
  creationMode?: boolean;
  isSellThroughHidden?: boolean;
  params: {
    influencerId: string;
    programId: string;
  };
  save: (delta?: Partial<CollectionProgram>) => Promise<boolean>;
  refetchCollection: () => void;
};

export const BackButton = () => (
  <div className="flex justify-start cursor-pointer" onClick={goBack}>
    <div className="bg-white p-3 rounded-xl border border-gray-200">
      <FontAwesomeIcon
        icon={faArrowLeft as IconProp}
        className="w-auto h-4 flex-no-shrink fill-current"
      />
    </div>
  </div>
);

export const Program = (
  p: ProgramPageProps & { tabProps: ProgramSummaryTabProps },
) => {
  const { tabHash, onTabChange } = useTabHash(keys(allTabs), 'about');
  const [isLandingPageDirty, setIsLandingPageDirty] = useState(false);

  const guardedTabChange = useCallback(
    (event: React.SyntheticEvent | null, value: ProgramTabHash) => {
      if (isLandingPageDirty) {
        const confirmed = window.confirm(
          'You have unsaved changes. Are you sure you want to leave without saving?',
        );

        if (!confirmed) return;

        setIsLandingPageDirty(false);
      }

      onTabChange(event, value);
    },
    [isLandingPageDirty, onTabChange],
  );

  return (
    <HeaderPage
      header={
        <>
          <div className="flex items-center gap-5">
            <BackButton />
            <Typography variant="h1">{p.tabProps.collection.name}</Typography>
            {/* <StatusTags tags={p.tags} sharedProps={{ big: true }} /> */}
          </div>
          {!p.publishAction ? null : (
            <div className="flex self-center">
              <Button
                color="neutral"
                onClick={p.publishAction.onClick}
                leftIcon={
                  p.publishAction.loading ? undefined : p.publishAction.icon
                }
                disabled={p.publishAction.disabled}
              >
                {p.publishAction.loading ? (
                  <CircularProgress className="text-white w-5 h-5" />
                ) : (
                  p.publishAction.title
                )}
              </Button>
            </div>
          )}
        </>
      }
    >
      <PublishModal {...p.publishModal} programCard={p.programCard} />
      <SuccessModal {...p.successModal} collapsibleLinks />
      <ValidationAlert {...p.validationErrors} />
      <SubscriptionPriceAlert {...p.subscriptionPriceAlert} />
      <ArchiveModal {...p.archiveModal} />
      <PublishToBundle {...p.publishToBundleModal} />
      <RemoveFromBundle {...p.removeFromBundleModal} />
      <Tabs
        value={getTabWithWorkoutDefault(tabHash)}
        onChange={guardedTabChange}
        variant="scrollable"
      >
        {keys(p.tabs)
          .map((name) => ({ name, label: allTabs[name], ...p.tabs[name] }))
          .map((t) => (
            <Tab
              key={t.name}
              value={t.name}
              label={t.label}
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
              iconPosition="start"
              icon={
                t.name === 'challenges' ? undefined : (
                  <FontAwesomeIcon
                    icon={t.isReady ? readyIcon : notReadyIcon}
                    {...(t.isReady
                      ? { color: stepStateColorsMap['ready'] }
                      : {})}
                  />
                )
              }
            />
          ))}
      </Tabs>
      <TabContent
        {...p.tabProps}
        tabHash={tabHash}
        workouts={p.tabProps.collection.workouts || []}
        params={{
          influencerId: p.tabProps.collection.influencerId + '',
          programId: p.tabProps.collection.id + '',
        }}
        readonly={p.readonly}
        onLandingPageDirtyChange={setIsLandingPageDirty}
      />
    </HeaderPage>
  );
};
